/**
 * WHAT CAME OF A CODE'S SEND, ASKED FOR A WHILE (the owner's word of 2026-10-20: "User messages
 * always gives info, never silent"). The Worker answers an ask for a code at once, before the code
 * is sent, so a code Microsoft or the phone system did not take could otherwise leave the page
 * saying "on its way" for a code that is not. Once the ask's 202 named the ask by its own id, the
 * code step asks the Worker what came of that one send (`codeSendState`), every second and a half
 * for a little more than thirty seconds, and is told only of a send that failed, with why, which it
 * says in place of "on its way". A send the Worker reads as sent, an ask it no longer holds, and the
 * end of the asking each end it quietly, as the step going away or asking again does, by the stop
 * this hands back. One request at a time: the next is only set once the last has answered.
 *
 * THIRTY SECONDS AND A LITTLE MORE (the review of 303dfc2). The Worker reads every send with no
 * failure written, and every ask for an address not on file, as `sending` until thirty seconds after
 * the ask and `sent` from then on (`SENT_AFTER_MS` in `api/src/lib/signInCode.ts`), past the longest
 * any send can take, so a slow send that went says nothing of who is on file. A failure can be
 * written at any time in those thirty seconds, so the asking goes on past them.
 *
 * AN ASK MADE BEFORE THE STEP WAS DRAWN, kept by the tab (`lib/fresh.ts`), is asked about at once
 * rather than a second and a half on, and the step is told once its first answer is no failure, so
 * it says "already on its way" only then.
 *
 * The Worker reads a send that went and an ask for an address not on file alike, so what this tells
 * the step says nothing of whether anybody is on file but for a send that failed, which the owner
 * accepted.
 */
import { codeSendState, type SendFailure } from '../api.ts';

/** How long after an ask, and after each answer, the next is asked: a second and a half. */
export const CHECK_EVERY_MS = 1_500;

/**
 * How many times it is asked at most: thirty-three seconds of asking, past the thirty after which
 * the Worker reads a send as sent, with two to spare for answers that did not come.
 */
export const CHECKS = 22;

/** How the asking starts, for an ask made before the step that asks was drawn. */
export interface WatchFrom {
  /** Asks the moment the asking starts, rather than a second and a half on. */
  readonly atOnce?: boolean;
  /**
   * Called once, at the first answer that is not a failure: the send is out, went, is no longer
   * held, or could not be read this time, any of which leaves a code that may be on its way.
   */
  readonly heard?: () => void;
}

/**
 * Asks what came of the send of `ask`, and calls `failed` once, with why, should it not have gone.
 * Answers the stop, after which nothing more is asked and nothing is said.
 */
export function watchCodeSent(
  ask: string,
  failed: (why: SendFailure) => void,
  { atOnce = false, heard }: WatchFrom = {},
): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let checks = 0;
  let toldHeard = heard === undefined;

  const check = async () => {
    timer = undefined;
    checks += 1;
    const state = await codeSendState(ask);
    if (stopped) return;
    if (typeof state === 'object') {
      stopped = true;
      failed(state.failed);
      return;
    }
    if (!toldHeard) {
      toldHeard = true;
      heard?.();
      if (stopped) return;
    }
    if (state === 'sent' || state === 'gone' || checks >= CHECKS) {
      stopped = true;
      return;
    }
    timer = setTimeout(() => void check(), CHECK_EVERY_MS);
  };

  if (atOnce) void check();
  else timer = setTimeout(() => void check(), CHECK_EVERY_MS);
  return () => {
    stopped = true;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
}
